import { proctoringUBALogger } from './trackingUtils';

const environmentConfig = {
  screenAllowed: 1,
  redtrictedApps: [
    'chrome',
    'msedge',
    'firefox',
    'ms-teams',
    'zoom',
    'google-meet',
    'slack',
    'brave',
    'AnyDesk',
  ],
};

export const blockedShortcuts = ['Alt+Space', 'Ctrl+Shift+Esc'];

export function buildGetRunningProcessWinCommand(
  config = environmentConfig.redtrictedApps
) {
  const processes = config.map((name) => `'${name}'`).join(', ');
  return `powershell -command "$runningProcesses = Get-Process -Name ${processes} -ErrorAction SilentlyContinue; $runningProcesses | Select-Object -Property Name  | ConvertTo-Csv -NoTypeInformation"`;
  // return `ps -A -o pid,comm | grep chrome `;
}

export function buildKillRunningProcessWinCommand(
  config = environmentConfig.redtrictedApps
) {
  const processes = config.map((name) => `'${name}'`).join(', ');
  return `powershell -command "Stop-Process -Name ${processes} -Force "`;
}

export function parseGetallProceessResult(data) {
  const newData = new Set(data.split('\r'));

  const valuesToRemove = ['"Name"', '\n', ''];

  valuesToRemove.forEach((value) => {
    newData.delete(value);
  });

  return newData;
}

export function parseMonitorInfo(data) {
  let filteredArr = data.split('\r');

  // Remove all unwanted elements
  filteredArr = filteredArr.filter(
    (item) => item.trim() !== '' && !item.includes('------')
  );

  // Remove leading and trailing newlines from each string
  filteredArr = filteredArr.map((item) => item.trim());
  filteredArr.shift();

  return filteredArr;
}

export function parseSystemNotificationResult(data) {
  const arr = data.split('\r');
  const toastEnabledValue = arr.find((item) => item.includes('ToastEnabled'));

  if (toastEnabledValue) {
    const parts = toastEnabledValue.trim().split(/\s+/);
    return parts[parts.length - 1];
  }

  return null;
}

export function removeCharsByVowelCount(text, string) {
  const vowels = 'aeiouAEIOU';
  let vowelCount = 0;

  for (let i = 0; i < string.length; i += 1) {
    if (vowels.indexOf(string[i]) !== -1) {
      vowelCount += 1;
    }
  }

  if (text.length > vowelCount) {
    return text.slice(0, -vowelCount);
  }
  return text;
}

export const TotalDisplaysInfoCommand = `powershell -command "Get-PnpDevice -Class monitor -presentOnly"`;
export const NotificationInfoCommand = `reg query "HKCU\\SOFTWARE\\Microsoft\\Windows\\CurrentVersion\\PushNotifications" /v ToastEnabled`;
export const FingerGesturesCommand = `reg add "HKCU\\Software\\Microsoft\\Windows\\CurrentVersion\\PrecisionTouchPad" /v ThreeFingerSlideEnabled /t REG_DWORD /d 0 /f && reg add "HKCU\\Software\\Microsoft\\Windows\\CurrentVersion\\PrecisionTouchPad" /v FourFingerSlideEnabled /t REG_DWORD /d 0 /f && taskkill /f /im explorer.exe && start explorer.exe`;
export const getDetailedOSinfo = () => {
  const { userAgent, maxTouchPoints } = navigator;
  const uad = navigator.userAgentData || null;
  const platform = (uad && uad.platform) || navigator.platform || '';
  let osType = 'Unknown';
  let deviceType = 'Desktop';

  // OS detection (Android/iOS first to catch iPad desktop mode)
  const iosLike =
    /CPU(?: iPhone)? OS\s?\d+[_.]/.test(userAgent) ||
    /Mobile\//.test(userAgent);
  const isAndroidOS = /Android/i.test(userAgent);
  const hasTouchEvent =
    typeof window !== 'undefined' && 'ontouchstart' in window;
  const hasIOSStandalone =
    typeof navigator !== 'undefined' && 'standalone' in navigator;
  const screenObj =
    typeof window !== 'undefined' && window.screen
      ? window.screen
      : { width: 0, height: 0 };
  const minCss = Math.min(screenObj.width || 0, screenObj.height || 0);
  const maxCss = Math.max(screenObj.width || 0, screenObj.height || 0);
  const dpr =
    typeof window !== 'undefined' && window.devicePixelRatio
      ? window.devicePixelRatio
      : 1;
  // Windows tablet heuristic: high DPR + tablet-like CSS dimensions
  const isLikelyWindowsTabletByScreen =
    /Windows/i.test(userAgent) &&
    dpr >= 1.5 &&
    minCss >= 600 &&
    minCss <= 1200 &&
    maxCss >= 800 &&
    maxCss <= 2000;
  // iPad screen heuristic: Mac platform + high DPR + iPad-like CSS dimensions
  const isLikelyIPadByScreen =
    (/Macintosh/i.test(userAgent) || /Mac/i.test(platform)) &&
    dpr >= 2 &&
    minCss >= 740 &&
    minCss <= 1024 &&
    maxCss >= 1024 &&
    maxCss <= 1366;
  const isIPadDesktopMode =
    /iPad/i.test(userAgent) ||
    /iPad/i.test(platform) ||
    ((/Macintosh/i.test(userAgent) || /Mac/i.test(platform)) &&
      (iosLike || hasTouchEvent || hasIOSStandalone || isLikelyIPadByScreen));
  const isIOSUA = /iPhone|iPod/i.test(userAgent) || isIPadDesktopMode;

  if (isAndroidOS) osType = 'Android';
  else if (isIOSUA) osType = 'iOS';
  else if (/Windows/i.test(userAgent)) osType = 'Windows';
  else if (/Macintosh/i.test(userAgent)) osType = 'MacOS';
  else if (/Linux/i.test(userAgent)) osType = 'Linux';

  // ---- Device detection ----
  const isAndroid = isAndroidOS;
  const isIPhone = /iPhone|iPod/i.test(userAgent);
  const isIPad = isIPadDesktopMode; // iPad (incl. desktop mode) without touchpoint dependency
  const isWindowsTablet =
    /Windows/i.test(userAgent) &&
    /(Surface|Touch|Tablet|ARM|ARM64|Tablet PC)/i.test(userAgent);
  const isMobileUA = /Mobi|Mobile/i.test(userAgent);
  const isMobileUAD = !!(uad && typeof uad.mobile === 'boolean' && uad.mobile);
  // Smart displays / TVs / Chromecast devices should be treated as Mobile (unsupported)
  const isSmartDisplay =
    /CrOS|SmartDisplay|NestHub|CrKey|GoogleTV|Android TV|BRAVIA|SMART-TV|Tizen|Web0S|WebOS|; TV|\(TV\)/i.test(
      userAgent
    );
  // Platform tokens that often remain in desktop-site mode on Chrome mobile
  const isMobilePlatformToken =
    /(Android|iPhone|iPad|iPod|armv8l|armv7l|aarch64)/i.test(platform);

  // Only Windows/MacOS/Linux desktops are eligible for Desktop; others are Mobile
  if (osType !== 'Windows' && osType !== 'MacOS' && osType !== 'Linux') {
    deviceType = 'Mobile';
  }

  // Override to Mobile for any strong mobile/tablet/smart-display signals, and Windows tablets/Surface
  if (
    isAndroid ||
    isIPhone ||
    isIPad ||
    isMobileUA ||
    isMobileUAD ||
    isSmartDisplay ||
    isWindowsTablet ||
    isLikelyWindowsTabletByScreen ||
    isMobilePlatformToken ||
    maxTouchPoints > 1
  ) {
    deviceType = 'Mobile';
  }

  try {
    const logData = {
      osType,
      deviceType,
      ua: userAgent,
      platform,
      uadPlatform: uad && uad.platform,
      uadMobile: uad && uad.mobile,
      maxTouchPoints,
      dpr,
      screen: `${minCss}x${maxCss}`,
      flags: JSON.stringify({
        iosLike,
        hasTouchEvent,
        hasIOSStandalone,
        isAndroidOS,
        isIOSUA,
        isIPadDesktopMode,
        isLikelyIPadByScreen,
        isWindowsTablet,
        isLikelyWindowsTabletByScreen,
        isMobileUA,
        isMobileUAD,
        isSmartDisplay,
        isMobilePlatformToken,
      }),
    };
    proctoringUBALogger('DETAILED_OSINFO', JSON.stringify(logData));
  } catch (e) {
    // no-op logging failure
  }

  return { osType, deviceType };
};

export const getOSinfo = () => {
  getDetailedOSinfo();
  const { userAgent, maxTouchPoints, platform } = navigator;
  let deviceType = 'Unknown';
  // iPad detection logic
  const isIPad =
    /iPad/.test(userAgent) ||
    (userAgent.includes('Macintosh') && maxTouchPoints > 1);

  // just check if it is android or not. be it a tablet or android phone or foldables
  let isAndroidInDesktopMode = false;
  if (platform) {
    isAndroidInDesktopMode = /Android|arm|aarch/i.test(platform);
  }

  if (/Mobi|Android/i.test(userAgent) || isIPad || isAndroidInDesktopMode) {
    deviceType = 'Mobile';
  } else {
    deviceType = 'Desktop';
  }

  let osType = 'Unknown';
  if (userAgent.indexOf('Win') !== -1) osType = 'Windows';
  if (userAgent.indexOf('Mac') !== -1) osType = 'MacOS';
  if (userAgent.indexOf('Linux') !== -1) osType = 'Linux';
  if (userAgent.indexOf('Android') !== -1) osType = 'Android';
  if (userAgent.indexOf('like Mac') !== -1) osType = 'iOS';

  try {
    const simpleLog = {
      osType,
      deviceType,
      ua: userAgent,
      maxTouchPoints,
    };
    proctoringUBALogger('SIMPLE_OSINFO', JSON.stringify(simpleLog));
  } catch (e) {
    // no-op logging failure
  }

  return { osType, deviceType };
};

export const encodeAccessCodeInUrl = (testUrl = '') => {
  if (!testUrl) return '';

  const [baseUrl, queryString] = testUrl.split('?');
  if (!queryString) return testUrl;

  // Split query params
  const params = queryString.split('&').map((param) => {
    if (param.startsWith('access_code=')) {
      const accessCode = param.split('access_code=')[1] || '';
      const encoded = encodeURIComponent(accessCode);
      return `access_code=${encoded}`;
    }
    return param;
  });

  return `${baseUrl}?${params.join('&')}`;
};
